import { useState } from "react";

interface UseTablePaginationOptions<T> {
  initialPageSize: number | undefined;
  sortedData: T[];
  /**
   * Return to page 1 whenever this changes. Pass the thing that decides *which*
   * rows are shown — a search term, a filter value.
   *
   * This used to key off the row count instead, which was wrong in both
   * directions. It missed the case where a search redefines the result set
   * without changing its size, leaving the reader on page 4 of results they just
   * replaced. And it fired on any shrink, which is the case that wants clamping,
   * not resetting — see below.
   *
   * It's a prop rather than a `data` identity check because plenty of callers
   * build their row array inline, and a new reference every render would pin
   * them to page 1 forever.
   */
  resetPageKey?: string | number | undefined;
  /**
   * The size of the whole set when `sortedData` is only the current page of
   * it. Pages are counted from this and the rows are shown as they are.
   */
  serverTotalRows?: number | undefined;
}

export function useTablePagination<T>({
  initialPageSize,
  sortedData,
  resetPageKey,
  serverTotalRows,
}: UseTablePaginationOptions<T>) {
  const paginated = initialPageSize !== undefined;
  const [currentPageSize, setCurrentPageSize] = useState(initialPageSize ?? 10);
  const [currentPage, setCurrentPage] = useState(0);
  // The reset happens in render, not in an effect: a server-paged table
  // reports its query from the page it renders with, and a reset that
  // arrived one effect later would report the old page first. React's
  // "adjust state while rendering" pattern: the key seen on the last render
  // is kept, and any change to it puts the page back to 1, including a change
  // back to a key seen before.
  const [lastResetPageKey, setLastResetPageKey] = useState(resetPageKey);
  if (resetPageKey !== lastResetPageKey) {
    setLastResetPageKey(resetPageKey);
    setCurrentPage(0);
  }

  const totalRows = serverTotalRows ?? sortedData.length;
  const totalPages = paginated
    ? Math.max(1, Math.ceil(totalRows / Math.max(1, currentPageSize)))
    : 1;

  // Clamp for display rather than resetting the state. When the data shrinks
  // past the current page — a refresh mid-edit, a row deleted, a transient
  // fetch — the reader is shown the new last page; and if the rows come back
  // they are returned to where they were, because the state was never touched.
  const displayPage = Math.min(currentPage, totalPages - 1);

  const displayData =
    paginated && serverTotalRows === undefined
      ? sortedData.slice(
          displayPage * currentPageSize,
          (displayPage + 1) * currentPageSize,
        )
      : sortedData;

  return {
    paginated,
    currentPageSize,
    currentPage: displayPage,
    totalPages,
    displayData,
    setCurrentPageSize,
    setCurrentPage,
  };
}
